import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormLayout,
  TextField,
  Select,
  Button,
  InlineStack,
  BlockStack,
  Text,
  Card,
  Badge,
  Spinner,
  Banner,
  Checkbox,
  Divider,
} from "@shopify/polaris";
import { AppDispatch, RootState } from "configureStore";
import {
  createOrderDelivery,
  updateOrderDelivery,
} from "../deliveries.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import { CreateDeliveryInput, DeliveryStatus, UpdateDeliveryInput } from "types/Delivery";
import { fetchDeliveryById, fetchAvailableReservations, AvailableReservationItem } from "services/deliveries.service";
import { getStatusBadgeTone } from "./DeliveriesSection";
import DatePicker from "features/datepicker/date-picker";

interface DeliveryFormProps {
  orderId: string;
  deliveryId?: string;
  mode: "create" | "edit" | "view";
  filterByProductId?: string;
}

interface LocalDeliveryItem {
  id?: string;
  product: any;
  productName?: string;
  sku?: string;
  warehouse?: any;
  warehouseName?: string;
  quantity: number;
  maxQuantity: number;
  baseQuantity?: number;
  reservationItem: string;
  reservationNumber?: number;
  orderLineType?: string | null;
  orderLineId?: string | null;
  notes?: string | null;
}

const STATUS_OPTIONS: { label: string; value: DeliveryStatus }[] = [
  { label: "Draft", value: "draft" },
  { label: "Planned", value: "planned" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
];

export default function DeliveryForm({
  orderId,
  deliveryId,
  mode,
  filterByProductId,
}: DeliveryFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();

  const { selectedDelivery } = useSelector(
    (state: RootState) => state.deliveriesReducer
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [status, setStatus] = useState<DeliveryStatus>("draft");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [carrier, setCarrier] = useState("");
  const [items, setItems] = useState<LocalDeliveryItem[]>([]);

  // Available reservation items
  const [availableReservations, setAvailableReservations] = useState<AvailableReservationItem[]>([]);
  const [selectedReservationItems, setSelectedReservationItems] = useState<Set<string>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});
  const autoSelectDoneRef = useRef(false);

  // Load available reservations for create/edit mode
  useEffect(() => {
    const loadAvailableReservations = async () => {
      if (!companyId || !orderId) return;
      
      try {
        const { availableItems } = await fetchAvailableReservations(companyId, orderId);
        setAvailableReservations(availableItems || []);
        // Initialize quantities with available quantities
        const quantities: Record<string, number> = {};
        (availableItems || []).forEach((item: AvailableReservationItem) => {
          quantities[item.id] = item.availableQuantity;
        });
        setItemQuantities(quantities);
      } catch (err) {
        console.error("Failed to load available reservations:", err);
      }
    };

    if (mode === "create" || mode === "edit") {
      loadAvailableReservations();
    }
  }, [companyId, orderId, mode]);

  const getProductId = (product: any) =>
    typeof product === "object" && product !== null ? product.id : product;

  const filteredAvailableReservations = useMemo(() => {
    if (!filterByProductId) return availableReservations;
    return availableReservations.filter(
      (resItem) => getProductId(resItem.product) === filterByProductId
    );
  }, [availableReservations, filterByProductId]);

  useEffect(() => {
    autoSelectDoneRef.current = false;
  }, [filterByProductId]);

  useEffect(() => {
    if (mode !== "create" || !filterByProductId) return;
    if (!filteredAvailableReservations.length) return;
    if (autoSelectDoneRef.current) return;

    const initialIds = filteredAvailableReservations
      .filter((resItem) => !items.some((di) => di.reservationItem === resItem.id))
      .map((resItem) => resItem.id);

    if (initialIds.length > 0) {
      setSelectedReservationItems(new Set(initialIds));
    }
    autoSelectDoneRef.current = true;
  }, [mode, filterByProductId, filteredAvailableReservations, items]);

  const availableReservationMap = useMemo(() => {
    return (availableReservations || []).reduce<Record<string, AvailableReservationItem>>(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {}
    );
  }, [availableReservations]);

  // Load delivery data for edit/view modes
  useEffect(() => {
    const loadDelivery = async () => {
      if ((mode === "edit" || mode === "view") && deliveryId && companyId) {
        setIsLoading(true);
        try {
          const { delivery } = await fetchDeliveryById(companyId, deliveryId);
          setStatus(delivery.status);
          setDeliveryDate(
            delivery.deliveryDate
              ? new Date(delivery.deliveryDate).toISOString().split("T")[0]
              : ""
          );
          setNotes(delivery.notes || "");
          setCarrier(delivery.carrier || "");
          // Map delivery items to local format
          const loadedItems: LocalDeliveryItem[] = (delivery.items || []).map((item: any) => ({
            id: item.id,
            product: item.product,
            productName: typeof item.product === "object" ? item.product.title : "",
            sku: typeof item.product === "object" ? item.product.sku : "",
            quantity: item.quantity,
            maxQuantity: item.quantity,
            baseQuantity: item.quantity,
            reservationItem: item.reservationItem || "",
            orderLineType: item.orderLineType,
            orderLineId: item.orderLineId,
            notes: item.notes,
          }));
          setItems(loadedItems);
        } catch (err: any) {
          setError(err.message || t("deliveries_error_loading"));
        } finally {
          setIsLoading(false);
        }
      }
    };

    // Use selectedDelivery from Redux if available
    if (selectedDelivery && selectedDelivery.id === deliveryId) {
      setStatus(selectedDelivery.status);
      setDeliveryDate(
        selectedDelivery.deliveryDate
          ? new Date(selectedDelivery.deliveryDate).toISOString().split("T")[0]
          : ""
      );
      setNotes(selectedDelivery.notes || "");
      setCarrier(selectedDelivery.carrier || "");
      const loadedItems: LocalDeliveryItem[] = (selectedDelivery.items || []).map((item: any) => ({
        id: item.id,
        product: item.product,
        productName: typeof item.product === "object" ? item.product.title : "",
        sku: typeof item.product === "object" ? item.product.sku : "",
        quantity: item.quantity,
        maxQuantity: item.quantity,
        baseQuantity: item.quantity,
        reservationItem: item.reservationItem || "",
        orderLineType: item.orderLineType,
        orderLineId: item.orderLineId,
        notes: item.notes,
      }));
      setItems(loadedItems);
    } else {
      loadDelivery();
    }
  }, [mode, deliveryId, companyId, selectedDelivery, t]);

  useEffect(() => {
    if (mode !== "edit") return;
    if (!items.length) return;

    let hasChanges = false;
    const updatedItems = items.map((item) => {
      if (!item.reservationItem) return item;
      const available = availableReservationMap[item.reservationItem];
      if (!available) return item;
      const reservedQty = available.quantity ?? 0;
      const deliveredQty = available.fulfilledQuantity || 0;
      const baseQty = item.baseQuantity ?? 0;
      const maxQuantity = Math.max(0, reservedQty - deliveredQty + baseQty);
      const nextQuantity = Math.min(item.quantity, maxQuantity);
      if (item.maxQuantity !== maxQuantity || item.quantity !== nextQuantity) {
        hasChanges = true;
      }
      return {
        ...item,
        maxQuantity,
        quantity: nextQuantity,
      };
    });

    if (hasChanges) {
      setItems(updatedItems);
    }
  }, [mode, items, availableReservationMap]);

  const handleClose = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleToggleReservationItem = (itemId: string) => {
    const newSelected = new Set(selectedReservationItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedReservationItems(newSelected);
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    const qty = parseFloat(value) || 0;
    // Find the reservation item to get max quantity
    const resItem = availableReservations.find(r => r.id === itemId);
    const maxQty = resItem?.availableQuantity || 0;
    // Clamp to max
    const clampedQty = Math.min(Math.max(0, qty), maxQty);
    setItemQuantities((prev) => ({ ...prev, [itemId]: clampedQty }));
  };

  const handleAddSelectedItems = () => {
    const newItems: LocalDeliveryItem[] = [];
    
    selectedReservationItems.forEach((itemId) => {
      const resItem = availableReservations.find((ri) => ri.id === itemId);
      if (resItem) {
        // Check if already in items (by reservationItem ID)
        const exists = items.some((i) => i.reservationItem === itemId);
        
        if (!exists) {
          const qty = itemQuantities[itemId] || resItem.availableQuantity;
          newItems.push({
            product: resItem.product,
            productName: resItem.product?.title || "",
            sku: resItem.product?.sku || "",
            warehouse: resItem.warehouse,
            warehouseName: resItem.warehouse?.name || "",
            quantity: qty,
            maxQuantity: resItem.availableQuantity,
            baseQuantity: 0,
            reservationItem: resItem.id,
            reservationNumber: resItem.reservationNumber,
            orderLineType: resItem.orderLineType || null,
            orderLineId: resItem.orderLineId || null,
            notes: "",
          });
        }
      }
    });

    if (newItems.length > 0) {
      setItems([...items, ...newItems]);
      setSelectedReservationItems(new Set());
    }
  };

  const handleRemoveItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemQuantityChange = (index: number, value: string) => {
    const qty = parseFloat(value) || 0;
    const newItems = [...items];
    const maxQty = newItems[index].maxQuantity || qty;
    newItems[index] = { ...newItems[index], quantity: Math.min(Math.max(0, qty), maxQty) };
    setItems(newItems);
  };

  const handleItemNotesChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], notes: value };
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (!companyId) return;

    if (items.length === 0) {
      setError(t("deliveries_error_no_items"));
      return;
    }

    const hasLegacyItems = items.some((item) => !item.reservationItem);
    const hasReservationItems = items.some((item) => item.reservationItem);
    if (hasLegacyItems && mode === "create") {
      setError(t("deliveries_error_missing_reservations"));
      return;
    }
    if (mode === "edit" && hasLegacyItems && hasReservationItems) {
      setError(t("deliveries_error_legacy_mix"));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (mode === "create") {
        const createData: CreateDeliveryInput = {
          deliveryDate: deliveryDate || null,
          notes: notes || null,
          carrier: carrier || null,
          items: items.map((item) => ({
            reservationItem: item.reservationItem,
            quantity: item.quantity,
            notes: item.notes || null,
          })),
        };
        await dispatch(
          createOrderDelivery(companyId, orderId, {
            ...createData,
          })
        );
      } else if (mode === "edit" && deliveryId) {
        const updatePayload: UpdateDeliveryInput = {
          status,
          deliveryDate: deliveryDate || null,
          notes: notes || null,
          carrier: carrier || null,
        };
        if (!hasLegacyItems) {
          updatePayload.items = items.map((item) => ({
            id: item.id,
            reservationItem: item.reservationItem,
            quantity: item.quantity,
            notes: item.notes || null,
          }));
        }
        await dispatch(updateOrderDelivery(companyId, deliveryId, updatePayload));
      }

      // Refresh order data (including deliveries)
      dispatch(fetchOrderDetails({ companyId, orderId }));
      handleClose();
    } catch (err: any) {
      setError(err.message || t("deliveries_error_saving"));
    } finally {
      setIsSaving(false);
    }
  };

  const isReadOnly = mode === "view";
  const canChangeStatus = mode === "edit";
  const canAddItems = mode === "create" || mode === "edit";

  // Translate status options
  const translatedStatusOptions = STATUS_OPTIONS.map((opt) => ({
    label: t(`deliveries_status_${opt.value}`),
    value: opt.value,
  }));

  const carrierOptions = useMemo(
    () => [
      { label: t("select_carrier"), value: "" },
      { label: t("carrier_pristatymas_a"), value: "Pristatymas A" },
      { label: t("carrier_pristatymas_b"), value: "Pristatymas B" },
    ],
    [t]
  );

  const carrierLabel =
    carrierOptions.find((option) => option.value === carrier)?.label ||
    carrier ||
    "-";

  // Filter out reservation items already in delivery
  const availableToAdd = filteredAvailableReservations.filter((resItem) => {
    return !items.some((di) => di.reservationItem === resItem.id);
  });

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        <FormLayout>
          {canChangeStatus && (
            <Select
              label={t("status")}
              options={translatedStatusOptions}
              value={status}
              onChange={(value) => setStatus(value as DeliveryStatus)}
              disabled={isReadOnly}
            />
          )}

          {isReadOnly && (
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {t("status")}:
              </Text>
              <Badge tone={getStatusBadgeTone(status)}>
                {t(`deliveries_status_${status}`)}
              </Badge>
            </InlineStack>
          )}

          {!isReadOnly ? (
            <Select
              label={t("carrier")}
              options={carrierOptions}
              value={carrier}
              onChange={setCarrier}
              disabled={isReadOnly}
            />
          ) : (
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {t("carrier")}:
              </Text>
              <Text as="span" variant="bodyMd">
                {carrierLabel}
              </Text>
            </InlineStack>
          )}

          <DatePicker
            title={t("deliveries_date")}
            selected={deliveryDate}
            onChange={(value) => {
              if (!value) setDeliveryDate("");
              else if (value instanceof Date)
                setDeliveryDate(value.toISOString().split("T")[0]);
              else setDeliveryDate(String(value));
            }}
            isRequired={false}
            disableDatesAfter=""
            readOnly={isReadOnly}
            fullWidth
          />

          <TextField
            label={t("notes")}
            value={notes}
            onChange={setNotes}
            multiline={3}
            autoComplete="off"
            disabled={isReadOnly}
          />
        </FormLayout>

        {/* Items in delivery */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("deliveries_items")} ({items.length})
            </Text>

            {items.length > 0 ? (
              <BlockStack gap="200">
                {items.map((item, index) => {
                  const productName =
                    item.productName ||
                    (typeof item.product === "object"
                      ? (item.product as any).title
                      : "") ||
                    "-";
                  const sku =
                    item.sku ||
                    (typeof item.product === "object"
                      ? (item.product as any).sku
                      : "") ||
                    "-";
                  const warehouseName =
                    item.warehouseName ||
                    (item.warehouse && typeof item.warehouse === "object"
                      ? (item.warehouse as any).name
                      : "") ||
                    "-";

                  return (
                    <Card key={item.id || `item-${index}`}>
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {productName}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            SKU: {sku} | {t("warehouse")}: {warehouseName}
                          </Text>
                          {item.reservationNumber && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              {t("reservation")} #{item.reservationNumber}
                            </Text>
                          )}
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center">
                          {!isReadOnly ? (
                            <>
                              <div style={{ width: "80px" }}>
                                <TextField
                                  label=""
                                  labelHidden
                                  value={String(item.quantity)}
                                  onChange={(v) => handleItemQuantityChange(index, v)}
                                  autoComplete="off"
                                  helpText={t("deliveries_max_quantity", { max: item.maxQuantity })}
                                />
                              </div>
                              <div style={{ minWidth: "160px" }}>
                                <TextField
                                  label={t("notes")}
                                  labelHidden
                                  value={item.notes || ""}
                                  placeholder={t("notes_placeholder")}
                                  onChange={(v) => handleItemNotesChange(index, v)}
                                  autoComplete="off"
                                />
                              </div>
                              <Button
                                tone="critical"
                                variant="plain"
                                onClick={() => handleRemoveItem(index)}
                              >
                                {t("remove")}
                              </Button>
                            </>
                          ) : (
                            <Text as="p" variant="bodyMd">
                              {t("quantity")}: {item.quantity}
                            </Text>
                          )}
                        </InlineStack>
                      </InlineStack>
                    </Card>
                  );
                })}
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">
                {t("deliveries_no_items")}
              </Text>
            )}
          </BlockStack>
        </Card>

        {/* Add items from reservations */}
        {canAddItems && (
          <>
            <Divider />
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">
                  {t("deliveries_add_from_reservations")}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("deliveries_add_from_reservations_description")}
                </Text>

                {availableToAdd.length > 0 ? (
                  <BlockStack gap="200">
                    {availableToAdd.map((resItem) => (
                      <Card key={resItem.id}>
                        <InlineStack align="space-between" blockAlign="center">
                          <Checkbox
                            label={
                              <BlockStack gap="100">
                                <Text as="span" variant="bodyMd">
                                  {resItem.product?.title || "-"}
                                </Text>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  SKU: {resItem.product?.sku || "-"} | {t("warehouse")}: {resItem.warehouse?.name || "-"}
                                </Text>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {t("reservation")} #{resItem.reservationNumber} | {t("deliveries_reserved")}: {resItem.quantity} | {t("deliveries_delivered")}: {resItem.fulfilledQuantity} | {t("deliveries_available")}: {resItem.availableQuantity}
                                </Text>
                              </BlockStack>
                            }
                            checked={selectedReservationItems.has(resItem.id)}
                            onChange={() => handleToggleReservationItem(resItem.id)}
                          />
                          <div style={{ width: "80px" }}>
                            <TextField
                              label=""
                              labelHidden
                              value={String(itemQuantities[resItem.id] || resItem.availableQuantity)}
                              onChange={(v) => handleQuantityChange(resItem.id, v)}
                              autoComplete="off"
                              disabled={!selectedReservationItems.has(resItem.id)}
                              helpText={t("deliveries_max_quantity", { max: resItem.availableQuantity })}
                            />
                          </div>
                        </InlineStack>
                      </Card>
                    ))}
                  </BlockStack>
                ) : (
                  <Banner tone="warning">
                    {t("deliveries_no_available_reservations")}
                  </Banner>
                )}

                {selectedReservationItems.size > 0 && (
                  <Button onClick={handleAddSelectedItems}>
                    {t("deliveries_add_selected")} ({selectedReservationItems.size})
                  </Button>
                )}
              </BlockStack>
            </Card>
          </>
        )}

        {/* Actions */}
        <InlineStack gap="200" align="end">
          <Button onClick={handleClose}>{t("cancel")}</Button>
          {!isReadOnly && (
            <Button variant="primary" submit loading={isSaving}>
              {mode === "create" ? t("create") : t("save")}
            </Button>
          )}
        </InlineStack>
      </BlockStack>
    </Form>
  );
}
